import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useWorkstation } from '../auth';
import { RefusalPanel } from '../components/RefusalPanel';
import { ReportSummary, useReport } from '../components/ReportSummary';
import { StateBadge } from '../components/StateBadge';
import { ZoomViewer } from '../components/ZoomViewer';
import type { Sheet } from '../model';
import {
  approvalCrops, cropLabel, downloadState, orientationSettled, reviewProgress,
} from '../review-gate';

/**
 * Where a human decides whether a sheet gets printed (F4, F5, F6).
 *
 * The rule this screen exists to enforce: at 145 pixels per inch a stitch is
 * eight pixels, so the whole error budget is under three of them, and at
 * fit-to-screen a perfect sheet and a badly slipped one look identical. The
 * zoom crops are the only place alignment is visible. Approve no longer waits
 * for every one to be opened (2026-10-02); the count opened is shown beside
 * the button, and the ticks show which.
 *
 * Orientation (D8) is now decided by the solver when the printer has been
 * surveyed, and the views are turned into page orientation, so what the
 * approver sees is the way the sheet comes out. Until a printer *is* surveyed
 * there is still nothing that can tell a design from the same design upside
 * down -- the weave is symmetric and the overlays are drawn in photo space --
 * so the two cases say different things here, and the cautious one is what is
 * shown while the report is still loading.
 */
export function Review() {
  const { jobId = '', sheetId = '' } = useParams();
  const api = useWorkstation();
  const navigate = useNavigate();

  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [jobName, setJobName] = useState<string | null>(null);
  const [overlays, setOverlays] = useState<Record<string, string>>({});
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [zoomed, setZoomed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getSheet(jobId, sheetId)
      .then((r) => {
        setSheet(r.sheet);
        setOverlays(r.overlays);
        setReportUrl(r.report);
        // A sheet reviewed earlier keeps credit for the crops already opened.
        setOpened(new Set(r.sheet.reviewedCrops ?? []));
      })
      .catch((e) => setError((e as Error).message));
  }, [jobId, sheetId]);

  // Only for the way back. The link works without it, so a failure is silent.
  useEffect(() => {
    api.getJob(jobId).then((j) => setJobName(j.name)).catch(() => {});
  }, [jobId]);

  const { report, error: reportError } = useReport(reportUrl);
  // Until the report has loaded, assume the cautious answer. A screen that says
  // orientation was checked when it does not yet know is worse than one that
  // asks for a second look it did not need.
  const orientation = report?.orientation_chosen;
  const settled = orientationSettled(report);

  // Every overlay the solver produced, `overlay_full` included.
  const crops = useMemo(() => approvalCrops(Object.keys(overlays)), [overlays]);
  // The grid shows the zoom crops; the whole sheet has its own panel above it.
  const zoomCrops = useMemo(
    () => crops.filter((n) => n !== 'overlay_full'), [crops]);
  const progress = reviewProgress({ crops, opened });

  function open(name: string) {
    setZoomed(name);
    setOpened((was) => new Set(was).add(name));
  }

  async function approve() {
    setBusy(true);
    try {
      await api.approve(jobId, sheetId, [...opened]);
      // Approving is the decision to print, so the PDF comes with it (client
      // request). The title-panel button stays for a second copy or a retry.
      await download();
      const fresh = await api.getSheet(jobId, sheetId);
      setSheet(fresh.sheet);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function reject() {
    const reason = window.prompt('Why is this sheet being sent back?') ?? '';
    setBusy(true);
    try {
      await api.reject(jobId, sheetId, reason);
      navigate(`/jobs/${jobId}`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function download() {
    try {
      const { url } = await api.pdf(jobId, sheetId);
      window.location.href = url;
    } catch (e) { setError((e as Error).message); }
  }

  const back = (
    <Link className="back-link" to={`/jobs/${jobId}`}>
      ← {jobName ?? 'Job'}
    </Link>
  );

  if (!sheet) {
    return (
      <div className="stack">
        {back}
        <p className="muted">{error ?? 'Loading…'}</p>
      </div>
    );
  }
  const approved = sheet.state === 'approved' || sheet.state === 'printed';
  const pdf = downloadState(sheet.state);

  return (
    <div className="stack">
      {back}
      <section className="panel">
        <div className="row-between">
          <div className="row-between">
            <h2>Sheet {sheet.sheetId}</h2>
            <StateBadge state={sheet.state} />
          </div>
          {/* Up here so it is the first thing seen once a sheet is approved. */}
          <div className="title-actions">
            <button className="primary" disabled={!pdf.enabled}
                    onClick={() => void download()}>
              Download print PDF
            </button>
          </div>
        </div>
        {pdf.note && <p className="muted download-note">{pdf.note}</p>}
        {sheet.approvedAt && (
          <p className="muted">
            Approved by {sheet.approvedBy} on{' '}
            {new Date(sheet.approvedAt).toLocaleString()}
          </p>
        )}
      </section>

      {sheet.refusal && <RefusalPanel refusal={sheet.refusal} />}

      {sheet.state === 'needs_review' && !settled && (
        <section className="panel warn">
          <h3>Check which way up it is</h3>
          <p>
            Nothing in the system decides this. The crops below are drawn on
            the photograph, where the design is always the right way up, so a
            sheet that will print upside down looks correct in every one of
            them. Compare the whole-sheet view against the design before
            approving.
          </p>
        </section>
      )}

      {sheet.state === 'needs_review' && settled && (
        <section className="panel">
          <h3>Which way up it will print</h3>
          <p>
            Decided from this printer&rsquo;s survey rather than from the
            photograph, so it does not depend on which end of the bed the
            photograph was taken from. The design&rsquo;s top prints at the top
            of the page, and the views below are turned to match &mdash; what you
            see is the way it comes out.
          </p>
          {Boolean(orientation?.preview_rotation_deg) && (
            <p className="muted">
              The photograph was taken {orientation?.preview_rotation_deg}&deg;
              from that, so these views are turned by the same amount.
            </p>
          )}
        </section>
      )}

      {reportUrl && (
        <section className="panel">
          <h3>What was measured</h3>
          <ReportSummary report={report} error={reportError} />
        </section>
      )}

      {overlays.overlay_full && (
        <section className="panel">
          <h3>Whole sheet{opened.has('overlay_full') ? ' ✓' : ''}</h3>
          <p className="muted">
            Placement, not alignment. Alignment is not visible at this size --
            but this is the only view where the design can be compared against
            the artwork as a whole.
          </p>
          <button type="button" className="full-overlay-button"
                  onClick={() => open('overlay_full')}>
            <img className="full-overlay" src={overlays.overlay_full}
                 alt="The whole sheet with the artwork overlaid" />
          </button>
        </section>
      )}

      <section className="panel">
        <h3>Zoom crops</h3>
        <div className="crops">
          {zoomCrops.map((name) => (
            <button key={name} type="button"
                    className={`crop ${opened.has(name) ? 'crop-seen' : ''}`}
                    onClick={() => open(name)}>
              <img src={overlays[name]} alt={cropLabel(name)} loading="lazy" />
              <span>{cropLabel(name)}{opened.has(name) ? ' ✓' : ''}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Approved sheets have nothing left to decide; the download is in the
          title panel. */}
      {!approved && (
        <section className="panel actions">
          {/* Beside the button, not under the crop grid: the count includes the
              whole-sheet view, so it is not a statement about the grid. */}
          <p className={progress.canApprove ? 'muted' : 'warn-text'}>
            {progress.hint}
          </p>
          <button className="primary" disabled={!progress.canApprove || busy}
                  onClick={() => void approve()}>
            Approve
          </button>
          <button disabled={busy} onClick={() => void reject()}>
            Send back for another photo
          </button>
        </section>
      )}

      {zoomed && (
        <ZoomViewer src={overlays[zoomed]} alt={cropLabel(zoomed)}
                    caption={cropLabel(zoomed)} onClose={() => setZoomed(null)} />
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}
